import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { AddressFormComponent } from '@spartacus/user/profile/components';
import { ADDRESS_TYPE } from '../../../../../../../core/models/address.model';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { switchMap, tap } from 'rxjs';
import {
  AddressValidation,
  Country,
  ErrorModel,
  GlobalMessageService,
  GlobalMessageType,
  Region,
  TranslationService,
  UserAddressService,
} from '@spartacus/core';
import { CustomValidatorService } from '../../../../../../../components/form-elements/custom-validator.service';
import { LaunchDialogService } from '@spartacus/storefront';
import { UserProfileFacade } from '@spartacus/user/profile/root';

@Component({
  selector: 'cx-address-form',
  templateUrl: './address-form.component.html',
  styleUrl: './address-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomAddressFormComponent extends AddressFormComponent implements OnInit {
  @Input() addressType: ADDRESS_TYPE = ADDRESS_TYPE.DROP_SHIP_ADDRESS;
  country?: Country;

  override addressForm: UntypedFormGroup = this.fb.group({
    companyName: ['', Validators.required],
    country: this.fb.group({
      isocode: [null, this.customValidatorService.required()],
    }),
    region: this.fb.group({
      isocode: [null, Validators.required],
    }),
    town: ['', Validators.required],
    line1: ['', Validators.required],
    line2: [''],
    complement: [''],
    postalCode: ['', Validators.required],
    dropShippingAddress: [true, Validators.required], // is Ship To Address
    phone: '',
  });

  override countries$ = this.userAddressService.getDeliveryCountries().pipe(
    tap((countries: Country[]) => {
      if (Object.keys(countries).length === 0) {
        this.userAddressService.loadDeliveryCountries();
      }
    }),
  );

  constructor(
    private _fb: UntypedFormBuilder,
    private _userAddressService: UserAddressService,
    private _globalMessageService: GlobalMessageService,
    private _translation: TranslationService,
    private _launchDialogService: LaunchDialogService,
    private _userProfileFacade: UserProfileFacade,
    private customValidatorService: CustomValidatorService,
  ) {
    super(_fb, _userAddressService, _globalMessageService, _translation, _launchDialogService, _userProfileFacade);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.configForm();
    // this.mockForm();
  }

  configForm() {
    this.addressForm.get('dropShippingAddress')?.setValue(this.addressType === ADDRESS_TYPE.DROP_SHIP_ADDRESS);

    if (this.addressData && Object.keys(this.addressData).length !== 0) {
      if (this.addressData?.country) {
        this.setCountry(this.addressData?.country);
      }
    }

    this.regions$ = this.selectedCountry$.pipe(
      switchMap(country => {
        return this.userAddressService.getRegions(country);
      }),
      tap((regions: Region[]) => {
        const regionControl = this.addressForm.get('region.isocode');
        if (regions && regions.length > 0) {
          regionControl?.enable();
        } else {
          regionControl?.disable();
        }

        if (this.addressData?.region) {
          this.addressForm.get('region')?.get('isocode')?.setValue(this.addressData.region.isocode);
        }
      }),
    );
  }

  setCountry(country?: Country) {
    const acronym = (country?.isocode || '').toUpperCase();
    this.addressForm.get('country')?.get('isocode')?.setValue(acronym);
    this.addressForm.get('region')?.get('isocode')?.setValue(null);
    this.country = country;
    this.selectedCountry$.next(acronym ?? '');
  }

  mockForm() {
    this.addressForm.get('companyName')?.setValue('NTT Data');
    this.addressForm.get('line1')?.setValue('Rua das Acacias');
    this.addressForm.get('line2')?.setValue('N. ' + new Date().getMilliseconds());
    this.addressForm.get('postalCode')?.setValue('87340-000');
    this.addressForm.get('town')?.setValue('Mamborê');
    this.addressForm.get('complement')?.setValue('Casa');
    this.addressForm.get('phone')?.setValue('1111111111');

    this.setCountry({
      diallingCode: '+375',
      isocode: 'BY',
      name: 'Belarus (Беларусь)',
      phoneMask: '(00) 000-00-00',
    });
    this.addressForm.get('companyName')?.markAsDirty();
    this.addressForm.markAsDirty();
  }

  validateInput(event: KeyboardEvent): void {
    const allowedChars = /[0-9()-]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (!allowedChars.test(inputChar)) {
      event.preventDefault();
    }
  }

  override verifyAddress(): void {
    if (this.addressForm.valid) {
      // const regionControl = this.addressForm.get('region');
      // const isocode = regionControl?.value?.isocode;

      // if (isocode) {
      //   this.regions$.pipe(take(1)).subscribe((regions: Region[]) => {
      //     if (regions.length) {
      //       const selectedRegion = regions.find((region: Region) => region.isocode === isocode);
      //       regionControl?.patchValue({
      //         isocodeShort: selectedRegion?.isocodeShort,
      //       });
      //     } else {
      //       regionControl?.reset();
      //     }
      //   });
      // }

      if (this.addressForm.dirty) {
        this.handleAddressVerificationResults({
          decision: 'ACCEPT',
        });
      } else {
        // address form value not changed
        // ignore duplicate address
        this.submitAddress.emit(undefined);
      }
    } else {
      this.addressForm.markAllAsTouched();
    }
  }

  override handleAddressVerificationResults(results: AddressValidation) {
    if (results.decision === 'ACCEPT') {
      this.submitAddress.emit(this.addressForm.value);

      if (this.addressType === ADDRESS_TYPE.SHIP_ADDRESS) {
        this.globalMessageService.add(
          { key: 'addressBook.shipAddress.successRequest' },
          GlobalMessageType.MSG_TYPE_CONFIRMATION,
        );
      }
    } else if (results.decision === 'REJECT') {
      if (results.errors?.errors.some((error: ErrorModel) => error.subject === 'titleCode')) {
        this.globalMessageService.add({ key: 'addressForm.titleRequired' }, GlobalMessageType.MSG_TYPE_ERROR);
      } else {
        this.globalMessageService.add({ key: 'addressForm.invalidAddress' }, GlobalMessageType.MSG_TYPE_ERROR);
      }
    } else if (results.decision === 'REVIEW') {
      this.openSuggestedAddress(results);
    }
  }
}
